import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";

const Contact = () => {
  const { lang, t } = useLang();
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
      setTimeout(() => setSent(false), 4500);
    } catch (err) {
      void err;
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="section-pad" style={{ paddingTop: "clamp(160px, 18vw, 220px)" }}>
        <div className="font-mono line-deco" style={{ fontSize: "0.75rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 32 }}>
          {t.contact.label}
        </div>
        <h1 className="font-display" style={{ fontSize: "clamp(3rem, 9vw, 8rem)", fontWeight: 300, lineHeight: 0.95, color: "var(--warm-off-white)", letterSpacing: "-0.02em" }}>
          {t.contact.title}
        </h1>
        <p className="font-body" style={{ color: "rgba(248,244,240,0.65)", lineHeight: 1.7, marginTop: 24, fontSize: "1.1rem", maxWidth: 640 }}>
          {t.contact.intro}
        </p>
      </section>

      <section className="section-pad" style={{ paddingTop: 0 }}>
        <div className="grid" style={{ gridTemplateColumns: "1fr 1.4fr", gap: 80, alignItems: "start" }}>
          <Reveal>
            <div className="font-mono line-deco" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 24 }}>
              {t.contact.info}
            </div>
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 24 }}>
              <li style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                <Mail size={18} color="var(--gold)" style={{ marginTop: 4 }} />
                <a href={`mailto:${settings.contact_email || "info@vakaragency.com"}`} className="link-underline" style={{ color: "var(--warm-off-white)", textDecoration: "none" }}>{settings.contact_email || "info@vakaragency.com"}</a>
              </li>
              <li style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                <Phone size={18} color="var(--gold)" style={{ marginTop: 4 }} />
                <span style={{ color: "var(--warm-off-white)" }}>{settings.contact_phone}</span>
              </li>
              <li style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                <MapPin size={18} color="var(--gold)" style={{ marginTop: 4 }} />
                <span style={{ color: "var(--warm-off-white)" }}>{lang === "tr" ? settings.address_tr : settings.address_en}</span>
              </li>
            </ul>
            <div style={{ marginTop: 48 }}>
              <div className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 8 }}>{t.contact.working}</div>
              <p style={{ color: "rgba(248,244,240,0.75)" }}>{lang === "tr" ? settings.working_hours_tr : settings.working_hours_en}</p>
            </div>
            <div style={{ marginTop: 32, padding: 24, background: "rgba(139,32,53,0.15)", border: "1px solid rgba(139,32,53,0.35)" }}>
              <p className="font-mono" style={{ fontSize: "0.7rem", letterSpacing: "0.2em", color: "var(--gold)", textTransform: "uppercase", marginBottom: 4 }}>24h</p>
              <p style={{ color: "var(--warm-off-white)" }}>{t.contact.response}</p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={submit} data-testid="contact-form" style={{ display: "flex", flexDirection: "column", gap: 28 }}>
              <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 28 }}>
                <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.form.name} className="input-line" />
                <input data-testid="form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact.form.email} className="input-line" />
                <input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.contact.form.phone} className="input-line" />
                <input data-testid="form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t.contact.form.company} className="input-line" />
                <select data-testid="form-service" required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input-line" style={{ appearance: "none" }}>
                  <option value="" style={{ background: "var(--rich-black)" }}>{t.contact.form.service}</option>
                  {services.map((s) => (
                    <option key={s.id} value={lang === "tr" ? s.title_tr : s.title_en} style={{ background: "var(--rich-black)" }}>
                      {lang === "tr" ? s.title_tr : s.title_en}
                    </option>
                  ))}
                </select>
                <select data-testid="form-budget" required value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input-line" style={{ appearance: "none" }}>
                  <option value="" style={{ background: "var(--rich-black)" }}>{t.contact.form.budget}</option>
                  {t.contact.form.budgets.map((b) => (
                    <option key={b} value={b} style={{ background: "var(--rich-black)" }}>{b}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="form-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.contact.form.message} className="input-line" style={{ resize: "vertical" }} />
              <button data-testid="form-submit" type="submit" disabled={sending} className="font-mono" style={{ padding: "18px 36px", background: sent ? "var(--gold)" : "var(--bordeaux)", color: sent ? "var(--rich-black)" : "var(--warm-off-white)", border: "none", textTransform: "uppercase", letterSpacing: "0.2em", fontSize: "0.75rem", cursor: sending ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12, alignSelf: "flex-start", transition: "background 0.4s var(--ease-cinema), color 0.4s var(--ease-cinema)" }}>
                {sent ? (<><Check size={16} /> {t.contact.form.success.split('.')[0]}</>) : sending ? t.contact.form.sending : (<>{t.contact.form.submit} <Send size={14} /></>)}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
